import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import PageMeta from "@/components/common/PageMeta";
import DailyLoggingCard from "./DailyLoggingCard";
import NeedsAttentionPanel from "./NeedsAttentionPanel";
import SnoozedAlerts from "./SnoozedAlerts";
import OnboardingChecklist from "./OnboardingChecklist";
import FlocksToActOn from "@/components/common/FlocksToActOn";
import { flockSnoozePayloads, type FlockPriority } from "./flockPriority";
import { deriveFlockStage, resolveSpentAge } from "./flockStage";
import { useIsViewer, useIsOwner } from "@/hooks/useRole";
import { useAuthStore } from "@/store/useAuthStore";
import { dashboardService } from "@/services/dashboardService";
import { handleApiError } from "@/services/api";
import { alertsService } from "@/services/alertsService";
import { onboardingService } from "@/services/onboardingService";
import { displayDate } from "@/lib/utils";
import { eggsOf, feedOf, deathsOf } from "./dayTiles";
import type { DashboardSummary, DashboardFlockDetail, Alert, AlertSnooze, OnboardingChecklist as OnboardingChecklistType } from "@/types/models";

// The arrow says which way the figure moved; the colour says whether that is
// good. They are separate on purpose: deaths used to be "inverted" by swapping
// current and previous, which turned the colour red but also turned the arrow,
// so deaths rising 0 → 8 read "↓8" (live walk 6, W6-2).
export function DeltaIndicator({
  current,
  previous,
  upIsBad = false,
}: {
  current: number;
  previous: number;
  /** For a measure where UP is bad — deaths. Changes the colour, never the arrow. */
  upIsBad?: boolean;
}) {
  if (previous === 0 && current === 0) return null;
  const diff = current - previous;
  if (diff === 0) return null;
  const isUp = diff > 0;
  const isGood = isUp !== upIsBad;
  return (
    <span
      className={`ml-2 inline-flex items-center text-xs font-medium ${
        isGood ? "text-success-500 dark:text-success-400" : "text-error-500 dark:text-error-400"
      }`}
    >
      {isUp ? "\u2191" : "\u2193"}
      {Math.abs(diff).toLocaleString()}
    </span>
  );
}

// A figure that owns a card. The day-over-day comparison lives on MiniStat
// now, where the three measured day-figures are; keeping a second unreachable
// copy of it here would be two implementations of one behaviour.
function StatCard({
  label,
  value,
  unit,
  highlight,
  sub,
}: {
  label: string;
  value: number | string;
  unit?: string;
  highlight?: boolean;
  /** A quiet second fact about the same thing — "2 flocks" under a bird
      count. It is not a second STAT: two numbers at the same weight in one
      card is the flatness this pass exists to remove. */
  sub?: string;
}) {
  return (
    <div className="card p-4 sm:p-5">
      <p className="stat-label">{label}</p>
      <p
        className={`mt-2 stat-value text-2xl sm:text-3xl ${
          highlight ? "!text-error-500 dark:!text-error-400" : ""
        }`}
      >
        {typeof value === "number" ? value.toLocaleString() : value}
        {unit && (
          <span className="ml-1 text-base font-normal text-gray-500 dark:text-gray-400">
            {unit}
          </span>
        )}
      </p>
      {sub && (
        <p className="mt-1.5 text-xs text-gray-500 num dark:text-gray-400">{sub}</p>
      )}
    </div>
  );
}

/* A figure that shares a surface with others rather than owning a card. Used
   along the foot of the hero, where three supporting numbers belong to the
   headline above them and would read as four equal claims in four cards. */
function MiniStat({
  label,
  value,
  unit,
  highlight,
  previousValue,
  upIsBad,
  prevLabel = "Yesterday",
}: {
  label: string;
  /** null = nothing was logged for the day: shown as "—", not as a zero. */
  value: number | null;
  unit?: string;
  highlight?: boolean;
  /** null = the previous day was not logged. */
  previousValue?: number | null;
  /** For a measure where UP is bad — deaths. */
  upIsBad?: boolean;
  /** Defaults, as StatCard's did. Optional in the type but functionally
      required once previousValue is given, so without a default a caller
      that passes one and not the other renders "undefined: 1,234 kg". */
  prevLabel?: string;
}) {
  return (
    <div className="px-5 py-4 sm:px-6">
      <p className="stat-label">{label}</p>
      <p
        className={`mt-1.5 text-lg font-semibold num sm:text-xl ${
          highlight
            ? "text-error-500 dark:text-error-400"
            : "text-gray-900 dark:text-white/90"
        }`}
      >
        {value === null ? "—" : value.toLocaleString()}
        {unit && value !== null && (
          <span className="ml-1 text-sm font-normal text-gray-500 dark:text-gray-400">
            {unit}
          </span>
        )}
        {value !== null && previousValue != null &&
          <DeltaIndicator current={value} previous={previousValue} upIsBad={upIsBad} />}
      </p>
      {value === null && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Not logged</p>
      )}
      {previousValue !== undefined && (
        <p className="mt-1 text-xs text-gray-500 num dark:text-gray-400">
          {prevLabel}:{" "}
          {previousValue === null
            ? "not logged"
            : `${previousValue.toLocaleString()}${unit ? ` ${unit}` : ""}`}
        </p>
      )}
    </div>
  );
}

export function FlockCard({ flock }: { flock: DashboardFlockDetail }) {
  // Per-farm spent-layer age (weeks), resolved to the default for an unset or
  // out-of-band value — mirrors the backend so the badge and cull alert agree.
  const spentAge = useAuthStore((s) =>
    resolveSpentAge(s.farm?.features?.spent_layer_age_weeks),
  );
  // Lifecycle stage from shed context + age (F1 uses shed_type; F4-obs1 adds
  // "spent" for very old layers). Spent flocks still produce (just declining),
  // so keep their egg metrics visible.
  const stageLabel = deriveFlockStage(flock.shed_type, flock.age_weeks, spentAge);
  const inBrooderShed = stageLabel === "brooding";
  const showEggMetrics =
    !inBrooderShed &&
    (stageLabel === "laying" ||
      stageLabel === "spent" ||
      (flock.hdp_percent ?? 0) > 0 ||
      (flock.today_eggs ?? 0) > 0);
  // Nothing logged for the day: say so, rather than 0% that reads like the
  // hens stopped laying.
  const noEntry =
    flock.today_feed_kg === null &&
    (!showEggMetrics || flock.today_eggs === null) &&
    flock.today_mortality === 0;
  const stageClass =
    stageLabel === "brooding"
      ? "bg-warning-50 text-warning-600 dark:bg-warning-500/10 dark:text-warning-400"
      : stageLabel === "spent"
        ? "bg-error-50 text-error-600 dark:bg-error-500/10 dark:text-error-400"
        : stageLabel === "laying"
          ? "bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400"
          : "bg-gray-100 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400";
  return (
    <Link to={`/flocks/${flock.flock_id}`} className="block card card-interactive p-5 sm:p-6">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-800 dark:text-white/90">
          {flock.batch_name}
        </h4>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${stageClass}`}>
          {stageLabel}
        </span>
      </div>

      <div className="mb-3 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
        <span>{flock.shed_code}</span>
        <span>{flock.age_weeks} wks</span>
        <span>{flock.current_count.toLocaleString()} birds</span>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {showEggMetrics && (
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-500">HDP%</p>
            <p className="text-lg font-bold tabular-nums text-gray-800 dark:text-white/90">
              {flock.hdp_percent === null ? "—" : `${flock.hdp_percent.toFixed(1)}%`}
            </p>
          </div>
        )}
        {showEggMetrics && (
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-500">Eggs</p>
            <p className="text-lg font-bold tabular-nums text-gray-800 dark:text-white/90">
              {flock.today_eggs === null ? "—" : flock.today_eggs.toLocaleString()}
            </p>
          </div>
        )}
        <div>
          <p className="text-xs text-gray-400 dark:text-gray-500">Feed</p>
          <p className="text-lg font-bold tabular-nums text-gray-800 dark:text-white/90">
            {flock.today_feed_kg === null ? (
              "—"
            ) : (
              <>
                {flock.today_feed_kg.toFixed(1)}
                <span className="text-xs font-normal text-gray-400"> kg</span>
              </>
            )}
          </p>
        </div>
        {flock.mortality_rate > 0 && (
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-500">Mortality</p>
            <p className="text-lg font-bold tabular-nums text-error-500 dark:text-error-400">
              {flock.mortality_rate.toFixed(1)}%
            </p>
          </div>
        )}
        {flock.today_mortality > 0 && (
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-500">Deaths</p>
            <p className="text-lg font-bold tabular-nums text-error-500 dark:text-error-400">
              {flock.today_mortality}
            </p>
          </div>
        )}
      </div>
      {noEntry && (
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">No entry for this day</p>
      )}
    </Link>
  );
}

type TrendPeriod = "7" | "30" | "fy";

// The financial year a YYYY-MM-DD day falls in, by its own calendar parts —
// not the browser's clock, so a past date picked on the dashboard gets ITS year.
function fyStartYear(day: string): number {
  const [y, m] = day.split("-").map(Number);
  return m >= 4 ? y : y - 1;
}

// Days in the window ending on `day`. The backend counts back from the day
// being viewed (from = day − (days − 1)), so FY is 1 April of that day's year
// up to that day — counting to the browser's today instead made a past date's
// window the wrong length.
function getTrendDays(period: TrendPeriod, day: string): number {
  if (period === "7") return 7;
  if (period === "30") return 30;
  const [y, m, d] = day.split("-").map(Number);
  const diff = (Date.UTC(y, m - 1, d) - Date.UTC(fyStartYear(day), 3, 1)) / 86_400_000;
  return Math.max(1, diff + 1);
}

function getTrendLabel(period: TrendPeriod, day: string): string {
  if (period === "7") return "7-Day";
  if (period === "30") return "30-Day";
  const fyYear = fyStartYear(day);
  return `FY ${fyYear}-${String(fyYear + 1).slice(2)}`;
}

// The window the trend-based cards cover, in words for a card's sub-line.
// Feed cost per kg and per egg are worked over this window, which ENDS on the
// day being viewed — without the words, ₹3.03 read as that day's figure (W6-3).
function trendWindowText(period: TrendPeriod, day: string, viewingToday: boolean): string {
  if (period === "fy") return `${getTrendLabel(period, day)} to ${viewingToday ? "date" : displayDate(day)}`;
  return viewingToday ? `last ${period} days` : `${period} days to ${displayDate(day)}`;
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function dateStrOffset(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isToday(dateStr: string): boolean {
  return dateStr === dateStrOffset(0);
}

function isYesterday(dateStr: string): boolean {
  return dateStr === dateStrOffset(1);
}

export default function Home() {
  const todayStr = dateStrOffset(0);
  const yesterdayStr = dateStrOffset(1);
  const isViewer = useIsViewer();
  const isOwner = useIsOwner();

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  // The setter was missing, which made the `if (error)` block below
  // unreachable dead code and every dashboard failure render as a blank page.
  // See the catch in the load effect.
  const [error, setError] = useState("");
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [snoozed, setSnoozed] = useState<AlertSnooze[]>([]);
  const [alertsError, setAlertsError] = useState(false);
  const [onboarding, setOnboarding] = useState<OnboardingChecklistType | null>(null);
  const [trendPeriod, setTrendPeriod] = useState<TrendPeriod>("7");
  // Default to yesterday: today's data is mostly empty until end-of-day
  // (no eggs collected yet, no mortality logged, etc.) so "0 / Yesterday:
  // 54,120" reads like production crashed. Yesterday is the most recent
  // complete day — one click on the Today chip switches to live data
  // when the user wants it.
  const [selectedDate, setSelectedDate] = useState(yesterdayStr);

  const viewingToday = isToday(selectedDate);
  const viewingYesterday = isYesterday(selectedDate);
  const viewingPreset = viewingToday || viewingYesterday;
  const dayLabel = viewingToday
    ? "Today"
    : viewingYesterday
      ? "Yesterday"
      : formatDateLabel(selectedDate);
  const prevDayLabel = viewingToday ? "Yesterday" : "Previous day";

  useEffect(() => {
    setLoading(true);
    // Always send the explicit date — the backend resolves an
    // undefined date param against its own UTC clock, which can
    // drift from the user's local "today" across the IST/UTC
    // boundary. With an explicit date the chip-click and
    // pick-this-same-date-in-the-picker paths always agree.
    dashboardService
      .getSummary(getTrendDays(trendPeriod, selectedDate), selectedDate)
      .then((s) => {
        setSummary(s);
        setError("");
      })
      // NOT `.catch(() => {})`. This screen has an error branch and the swallow
      // made it unreachable: a failed request left `summary` null, so the page
      // rendered the sidebar and NOTHING ELSE — no chart, no message, no clue.
      // "The dashboard is blank" was unanswerable from inside the app, which is
      // the same defect the activity log had. A request that fails must say so.
      .catch((e) => setError(handleApiError(e)))
      .finally(() => setLoading(false));
  }, [trendPeriod, selectedDate]);

  // Alerts are farm-current (not date-scoped), so load once. Only owner/viewer can
  // read them (the endpoint's roles) — other roles would 403, so don't fetch. A
  // real fault surfaces a note in the panel rather than a silent false "all clear".
  // Snoozed ones are already filtered server-side out of getAlerts; getSnoozed
  // powers the "Snoozed" list so the owner can unsnooze.
  const loadAlerts = useCallback(() => {
    if (!isOwner && !isViewer) return;
    alertsService
      .getAlerts()
      .then((a) => {
        setAlerts(a);
        setAlertsError(false);
      })
      .catch(() => setAlertsError(true));
    alertsService
      .getSnoozed()
      .then(setSnoozed)
      .catch(() => setSnoozed([]));
  }, [isOwner, isViewer]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  // New-owner setup checklist — owner-only (the endpoint's role); best-effort so a
  // failure just hides the card. Auto-vanishes once the farm's essentials exist.
  useEffect(() => {
    if (!isOwner) return;
    onboardingService
      .get()
      .then(setOnboarding)
      .catch(() => setOnboarding(null));
  }, [isOwner]);

  // Snooze mutes an alert (dashboard + digest). Optimistically drop the row, then
  // reconcile with the server (which also refreshes the "Snoozed" list). Only
  // owners can snooze; viewers see the panel without the control. This handler is
  // for the operational panel (per-alert).
  const handleSnooze = (alert: Alert, days?: number) => {
    setAlerts((prev) => prev.filter((a) => !(a.rule_id === alert.rule_id && a.flock_id === alert.flock_id)));
    const label = `${alert.flock_name ? `${alert.flock_name}: ` : ""}${alert.title}`;
    alertsService
      .snooze({ rule_id: alert.rule_id, flock_id: alert.flock_id, label, days })
      .then(loadAlerts)
      .catch(loadAlerts); // on failure, reload to restore the true state
  };

  // Muting a flock from the "Flocks to act on" card snoozes every alert it's
  // currently firing (spent + mortality + …). Optimistically drop the flock's
  // alerts, then reconcile. Flock alerts may be snoozed indefinitely. allSettled
  // (not all) so the reconcile runs after EVERY request settles — a partial
  // failure won't leave a half-applied state hanging until the next reload.
  const handleSnoozeFlock = (flock: FlockPriority, days?: number) => {
    setAlerts((prev) => prev.filter((a) => a.flock_id !== flock.flock_id));
    Promise.allSettled(flockSnoozePayloads(flock, days).map((p) => alertsService.snooze(p))).then(loadAlerts);
  };

  const handleUnsnooze = (snooze: AlertSnooze) => {
    setSnoozed((prev) => prev.filter((s) => !(s.rule_id === snooze.rule_id && s.flock_id === snooze.flock_id)));
    alertsService.unsnooze(snooze.rule_id, snooze.flock_id).then(loadAlerts).catch(loadAlerts);
  };

  const trendCount = summary?.trends.length ?? 0;
  const tickInterval = trendCount <= 14 ? 1 : trendCount <= 60 ? 7 : 30;

  const trendColors = ["#35798c", "#23734b", "#e0a526", "#b23a2e"];
  const trendValues = [
    summary?.trends.map((t) => t.hdp_percent) ?? [],
    summary?.trends.map((t) => t.total_eggs) ?? [],
    summary?.trends.map((t) => t.total_feed_kg) ?? [],
  ];
  const loneMarkers = trendValues.flatMap((values, seriesIndex) =>
    values.flatMap((v, i) =>
      v != null && values[i - 1] == null && values[i + 1] == null
        ? [{ seriesIndex, dataPointIndex: i, size: 4, fillColor: trendColors[seriesIndex], strokeColor: "#fff" }]
        : [],
    ),
  );

  // The shared tooltip runs these formatters on a gap (null) too.
  const fmtAxis = (val: number | null) => (val == null ? "—" : val.toFixed(0));

  const chartOptions: ApexOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: { curve: "smooth", width: 2 },
    // A logged day with gaps on both sides is a line of zero length — without
    // a marker it is invisible, and a farm's first entry looks like no data.
    // Only those days get one: a dot on every day of the FY view is ~500 dots.
    markers: { size: 0, hover: { size: 5 }, discrete: loneMarkers },
    xaxis: {
      categories: summary?.trends.map((t) => t.date.slice(5)) ?? [],
      labels: { style: { fontSize: "11px" }, rotate: -45, rotateAlways: trendCount > 14 },
      tickAmount: Math.ceil(trendCount / tickInterval),
    },
    yaxis: [
      {
        title: { text: "HDP %" },
        min: 0,
        labels: {
          formatter: fmtAxis,
        },
      },
      // Eggs and Feed SHARE this axis — `seriesName` names the series each
      // entry belongs to, and without a third entry the third series had no
      // axis at all. Both are counts/kg on the same right-hand scale, so
      // `show: false` on the duplicate draws one axis for the pair rather than
      // two identical ones.
      {
        opposite: true,
        seriesName: "Eggs",
        title: { text: "Count / kg" },
        labels: {
          formatter: fmtAxis,
        },
      },
      {
        opposite: true,
        seriesName: "Eggs",
        show: false,
        labels: {
          formatter: fmtAxis,
        },
      },
    ],
    legend: { position: "top" },
    tooltip: { shared: true, intersect: false },
    colors: trendColors,
  };

  // `t.hdp_percent.toFixed(1)` threw on a payload missing the field, and a
  // TypeError in render reaches the error boundary — so the ENTIRE app was
  // replaced by "Something went wrong" because one number was absent. The
  // service worker caches /api/* NetworkFirst for 24h, so a payload written by
  // an older backend can legitimately reach this code after a deploy.
  //
  // A missing measure renders as a GAP in the line (null), which is what
  // ApexCharts draws for a hole in a series — not 0, which would be a
  // measurement the farm never made.
  const num = (v: unknown, dp = 1): number | null =>
    typeof v === "number" && Number.isFinite(v) ? Number(v.toFixed(dp)) : null;

  // THE EMPTY STATE WAS UNREACHABLE. The guard used to be `trends.length > 0`,
  // and GetTrends runs on a generate_series — it emits ONE ROW PER DAY whether
  // or not anything was logged. So the length is the width of the window, never
  // zero, and "No trend data for this period." could not render for the one
  // case it exists for. A farm that has logged nothing got three flat lines
  // along the axis, which reads as a broken chart rather than an empty one.
  //
  // Ask whether anything was MEASURED, not whether rows came back. Mortality is
  // deliberately included: a period with only deaths recorded is a real, and
  // grim, thing to plot.
  const hasTrendData = (summary?.trends ?? []).some(
    (t) =>
      (t.total_eggs ?? 0) > 0 ||
      (t.total_feed_kg ?? 0) > 0 ||
      (t.mortality_count ?? 0) > 0,
  );
  // `last_log_date === null` means nothing has EVER been recorded — a different
  // sentence from "nothing in this period", and the one a farm sees after an
  // import or a reset.
  const neverLogged = summary?.freshness?.last_log_date == null;
  // A day nobody logged is "not logged", not zero (live walk 2, W2-18 — the
  // tile version of F8).
  const eggsToday = summary ? eggsOf(summary.today) : 0;
  const eggsPrev = summary ? eggsOf(summary.yesterday) : 0;
  // Viewers are read-only: show them WHY it is empty, but not a CTA they
  // cannot act on. Same rule the daily-logging card already applies.
  const canLogProduction = !isViewer;

  const chartSeries = [
    {
      name: "HDP %",
      type: "line" as const,
      data: summary?.trends.map((t) => num(t.hdp_percent)) ?? [],
    },
    {
      name: "Eggs",
      type: "line" as const,
      data: summary?.trends.map((t) => num(t.total_eggs, 0)) ?? [],
    },
    {
      name: "Feed (kg)",
      type: "line" as const,
      data: summary?.trends.map((t) => num(t.total_feed_kg)) ?? [],
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500 dark:text-gray-400">
        Loading dashboard...
      </div>
    );
  }

  if (error) {
    return (
      <>
        <PageMeta
          title="Dashboard | Poultry Book"
          description="Poultry farm management dashboard"
        />
        <div className="card p-6">
          <p className="text-error-500">{error}</p>
        </div>
      </>
    );
  }

  const fs = summary?.flock_summary;

  return (
    <>
      <PageMeta
        title="Dashboard | Poultry Book"
        description="Poultry farm management dashboard"
      />

      {/* New-owner setup checklist — shown at the top until the essentials exist,
          then it disappears. */}
      <OnboardingChecklist checklist={onboarding} />

      {/* The headline.
          One number, at display size, with the day's other measured figures
          along the foot of the same surface. Before this the dashboard opened
          with nine identically weighted cards, so nothing on it claimed to
          matter more than anything else.

          The hero is EGGS COLLECTED — a count the farm actually made. Hen-day
          production would be the better headline and is deliberately not
          derived here: the backend computes HDP per flock against a specific
          denominator, and a farm-level rate invented in the UI would divide by
          a different one. Two definitions of the same word, one click apart,
          is the livability bug this codebase already had to unpick. */}
      <section className="card mb-6 overflow-hidden">
        <div className="flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-start lg:justify-between lg:gap-8">
          <div>
            <p className="stat-label">Eggs collected · {dayLabel}</p>
            <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="stat-hero">
                {eggsToday === null ? "—" : eggsToday.toLocaleString()}
              </span>
              {eggsToday === null && (
                <span className="text-sm text-gray-500 dark:text-gray-400">Not logged</span>
              )}
              {eggsToday !== null && eggsPrev !== null && (
                <DeltaIndicator current={eggsToday} previous={eggsPrev} />
              )}
            </div>
            <p className="mt-2 text-sm text-gray-500 num dark:text-gray-400">
              {prevDayLabel}: {eggsPrev === null ? "not logged" : eggsPrev.toLocaleString()}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-gray-200 p-0.5 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setSelectedDate(yesterdayStr)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  viewingYesterday
                    ? "bg-brand-500 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white/80"
                }`}
              >
                Yesterday
              </button>
              <button
                type="button"
                onClick={() => setSelectedDate(todayStr)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  viewingToday
                    ? "bg-brand-500 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white/80"
                }`}
              >
                Today
              </button>
            </div>
            <input
              type="date"
              value={selectedDate}
              max={todayStr}
              onChange={(e) => setSelectedDate(e.target.value)}
              onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
              className={`h-8 w-[130px] rounded-lg border bg-transparent px-2 text-xs text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:bg-gray-900 dark:text-white/90 ${
                viewingPreset
                  ? "border-gray-300 dark:border-gray-700"
                  : "border-brand-500 dark:border-brand-500"
              }`}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 divide-x divide-gray-200 border-t border-gray-200 dark:divide-white/10 dark:border-white/10">
          <MiniStat
            label={`Feed ${dayLabel}`}
            value={summary ? feedOf(summary.today) : 0}
            unit="kg"
            previousValue={summary ? feedOf(summary.yesterday) : undefined}
            prevLabel={prevDayLabel}
          />
          <MiniStat
            label={`Deaths ${dayLabel}`}
            value={summary ? deathsOf(summary.today) : 0}
            highlight={(summary?.today.total_mortality ?? 0) > 0}
            previousValue={summary ? deathsOf(summary.yesterday) : undefined}
            upIsBad
            prevLabel={prevDayLabel}
          />
          <MiniStat label="Live birds" value={fs?.total_hens ?? 0} />
        </div>
      </section>

      {/* Daily-logging nudge — one surface: how many complete days behind (a
          lapse stales every metric) plus the per-flock/date checklist of what's
          missing. Anchored to the real calendar, today excluded. */}
      {summary && (
        <DailyLoggingCard
          daysBehind={summary.freshness?.days_behind ?? null}
          lastLogDate={summary.freshness?.last_log_date ?? null}
          entries={summary.missing_entries ?? []}
          canLog={!isViewer}
        />
      )}

      {/* Flocks needing action — ranked, with the recommended move. Owns every
          flock problem (the panel below is operational-only), so nothing is listed
          twice. Owners can mute a flock here; viewers get no snooze control. */}
      <FlocksToActOn alerts={alerts} onSnooze={isViewer ? undefined : handleSnoozeFlock} />

      {/* Operational alerts only — low stock, overdue money (flocks live in the
          card above). Owners can snooze; viewers get no snooze control. */}
      <NeedsAttentionPanel alerts={alerts} error={alertsError} onSnooze={isViewer ? undefined : handleSnooze} />

      {/* What the owner has muted, with an unsnooze action. */}
      {!isViewer && <SnoozedAlerts snoozes={snoozed} onUnsnooze={handleUnsnooze} />}

      {/* The farm's composition and its unit economics — real numbers, but
          answers to "what is the farm made of", not to "what happened today".
          They sit below the hero at card weight rather than beside it, because
          a flock count and a day's egg count are not the same kind of fact and
          rendering them identically was most of why this screen read flat. */}
      {(fs || summary?.advanced) && (
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
          {fs && <StatCard label="Active Flocks" value={fs.active_flocks} />}
          {fs && fs.active_layers > 0 && (
            <StatCard
              label="Layer birds"
              value={fs.layer_hens}
              sub={`${fs.active_layers} ${fs.active_layers === 1 ? "flock" : "flocks"}`}
            />
          )}
          {fs && fs.active_brooders > 0 && (
            <StatCard
              label="Brooder birds"
              value={fs.brooder_birds}
              sub={`${fs.active_brooders} ${fs.active_brooders === 1 ? "flock" : "flocks"}`}
            />
          )}
          {/* Advanced metrics. No per-role gate: this page is only rendered
              for owner and viewer — DashboardEntry sends a manager or
              supervisor to StaffDashboard, which has no money in it. */}
          {(summary?.advanced?.feed_cost_per_kg ?? 0) > 0 && (
            <StatCard
              label="Feed cost / kg"
              value={`\u20B9${summary!.advanced.feed_cost_per_kg.toFixed(2)}`}
              sub={`feed made, ${trendWindowText(trendPeriod, selectedDate, viewingToday)}`}
            />
          )}
          {(summary?.advanced?.feed_cost_per_egg ?? 0) > 0 && (
            <StatCard
              label="Feed cost / egg"
              value={`\u20B9${summary!.advanced.feed_cost_per_egg.toFixed(2)}`}
              sub={`feed made ÷ eggs, ${trendWindowText(trendPeriod, selectedDate, viewingToday)}`}
            />
          )}
          {/* Not highlighted. `highlight` paints a figure in error red, and
              this one was painted red unconditionally — so a farm running 0.2%
              cumulative mortality, which is a good flock, read as an alarm.
              Red has to mean a line was crossed, and there is no line here:
              the alert engine owns mortality thresholds and has real ones. */}
          {(summary?.advanced?.overall_mortality_rate ?? 0) > 0 && (
            <StatCard
              label="Overall mortality"
              value={`${summary!.advanced.overall_mortality_rate.toFixed(1)}%`}
            />
          )}
        </div>
      )}
      {/* Per-Flock Detail Cards */}
      {summary && summary.flocks && summary.flocks.length > 0 && (
        <div className="mb-6">
          <h3 className="mb-4 text-base font-semibold text-gray-800 dark:text-white/90">
            Flock Performance ({dayLabel})
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {summary.flocks.map((flock) => (
              <FlockCard key={flock.flock_id} flock={flock} />
            ))}
          </div>
        </div>
      )}

      {/* Trend Chart */}
      <div className="mb-6 card p-5 sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">
            {getTrendLabel(trendPeriod, selectedDate)} Production Trends
          </h3>
          <div className="flex gap-1 rounded-lg border border-gray-200 p-0.5 dark:border-gray-700">
            {([["7", "7D"], ["30", "30D"], ["fy", "FY"]] as const).map(([val, label]) => (
              <button
                key={val}
                onClick={() => setTrendPeriod(val)}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  trendPeriod === val
                    ? "bg-brand-500 text-white"
                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {hasTrendData ? (
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="line"
            height={280}
          />
        ) : (
          <div className="py-10 text-center">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {neverLogged
                ? "No production has ever been logged for this farm."
                : trendPeriod === "fy"
                  ? `Nothing was logged in ${getTrendLabel(trendPeriod, selectedDate)} up to ${displayDate(selectedDate)}.`
                  : `Nothing was logged in the ${trendPeriod} days to ${displayDate(selectedDate)}.`}
            </p>
            <p className="mt-1 text-xs text-gray-400">
              {neverLogged
                ? "The flocks and bird counts are recorded, but no eggs, feed or mortality have been entered — so there is nothing to chart."
                : "The chart is empty because no eggs, feed or mortality were recorded in this period — not because the data is missing."}
            </p>
            {canLogProduction && (
              <Link
                to="/daily-report"
                className="mt-3 inline-block rounded-lg bg-gray-900 dark:bg-white px-3 py-2 text-sm font-medium text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100"
              >
                Log production &rarr;
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Expense Summary & Mortality by Cause side by side */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Expense Breakdown */}
        {summary?.expense_summary && summary.expense_summary.total_amount > 0 && (
          <div className="card p-5 sm:p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">
                Expenses ({getTrendLabel(trendPeriod, selectedDate)})
              </h3>
              <span className="text-lg font-bold text-gray-800 dark:text-white/90">
                {"\u20B9"}{summary.expense_summary.total_amount.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
            </div>
            <ul className="space-y-2">
              {summary.expense_summary.by_category.map((cat) => {
                const pct = summary.expense_summary.total_amount > 0
                  ? (cat.amount / summary.expense_summary.total_amount) * 100
                  : 0;
                return (
                  <li key={cat.category}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {cat.category}
                      </span>
                      <span className="text-sm font-medium text-gray-800 dark:text-white/90">
                        {"\u20B9"}{cat.amount.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                      <div
                        className="h-full rounded-full bg-brand-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Mortality by Cause */}
        {summary && Object.keys(summary.mortality_by_cause).length > 0 && (
          <div className="card p-5 sm:p-6">
            <h3 className="mb-4 text-base font-semibold text-gray-800 dark:text-white/90">
              Mortality by Cause ({getTrendLabel(trendPeriod, selectedDate)})
            </h3>
            <ul className="space-y-2">
              {Object.entries(summary.mortality_by_cause)
                .sort(([, a], [, b]) => b - a)
                .map(([cause, count]) => (
                  <li
                    key={cause}
                    className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-2 dark:bg-gray-800/50"
                  >
                    <span className="text-sm capitalize text-gray-600 dark:text-gray-400">
                      {cause.replace(/_/g, " ")}
                    </span>
                    <span className="text-sm font-medium text-gray-800 dark:text-white/90">
                      {count}
                    </span>
                  </li>
                ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
}
